"use client";

import {
  Component,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Activity,
  ArrowRight,
  ArrowUpRight,
  Bell,
  BookOpen,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Command,
  Download,
  FileText,
  FolderKanban,
  Folders,
  Globe2,
  Inbox,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Menu,
  Moon,
  Package,
  Palette,
  Plus,
  Search,
  Settings2,
  ShieldCheck,
  ShoppingBag,
  SlidersHorizontal,
  Sparkles,
  Sun,
  TrendingUp,
  Users,
  Workflow,
  X,
  Zap,
} from "lucide-react";
import {
  createWorkspace,
  downloadFile,
  loadWorkspace,
  saveWorkspace,
  validateWorkspace,
  type Workspace,
} from "../../core/src";
import type { PageProps } from "./types";
import Dashboard, { AnalyticsPage } from "./pages/Dashboard";
import {
  ContactsPage,
  PipelinePage,
  ProjectsPage,
  TasksPage,
} from "./pages/Business";
import {
  CalendarPage,
  FilesPage,
  InboxPage,
  InvoicesPage,
  OrdersPage,
  ProductsPage,
  TeamPage,
} from "./pages/Operations";
import {
  AccentPicker,
  accentInk,
  accentValue,
  colorways,
  colorwayKey,
  layouts,
  LibraryPage,
  SettingsPage,
  StudioPage,
} from "./pages/Workspace";
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  Field,
  IconButton,
  Modal,
} from "./components/ui";

type Toast = {
  id: number;
  message: string;
  kind: "success" | "error" | "info";
};
type Session = {
  user: { id: string; email: string; name: string; role: string };
  csrfToken: string;
};
const navGroups = [
  {
    label: "WORKSPACE",
    items: [
      { id: "overview", name: "Overview", icon: LayoutDashboard },
      { id: "analytics", name: "Analytics", icon: TrendingUp },
      { id: "projects", name: "Projects", icon: FolderKanban },
      { id: "tasks", name: "Task board", icon: LayoutGrid },
    ],
  },
  {
    label: "BUSINESS",
    items: [
      { id: "contacts", name: "Contacts", icon: Users },
      { id: "pipeline", name: "Sales pipeline", icon: Workflow },
      { id: "products", name: "Products", icon: Package },
      { id: "orders", name: "Orders", icon: ShoppingBag },
      { id: "invoices", name: "Invoices", icon: FileText },
    ],
  },
  {
    label: "COLLABORATE",
    items: [
      { id: "calendar", name: "Calendar", icon: CalendarDays },
      { id: "inbox", name: "Inbox", icon: Inbox },
      { id: "files", name: "Files", icon: Folders },
      { id: "team", name: "Team", icon: Users },
    ],
  },
  {
    label: "MAKE IT YOURS",
    items: [
      { id: "studio", name: "Layout studio", icon: Palette },
      { id: "library", name: "UI library", icon: BookOpen },
      { id: "settings", name: "Settings", icon: Settings2 },
    ],
  },
];
const allNav = navGroups.flatMap((g) => g.items);
const translations: Record<string, Record<string, string>> = {
  fr: {
    overview: "Vue d’ensemble",
    analytics: "Analyses",
    projects: "Projets",
    tasks: "Tableau des tâches",
    contacts: "Contacts",
    pipeline: "Opportunités",
    products: "Produits",
    orders: "Commandes",
    invoices: "Factures",
    calendar: "Calendrier",
    inbox: "Messagerie",
    files: "Fichiers",
    team: "Équipe",
    studio: "Mises en page",
    library: "Composants",
    settings: "Paramètres",
  },
  ar: {
    overview: "نظرة عامة",
    analytics: "التحليلات",
    projects: "المشاريع",
    tasks: "لوحة المهام",
    contacts: "جهات الاتصال",
    pipeline: "المبيعات",
    products: "المنتجات",
    orders: "الطلبات",
    invoices: "الفواتير",
    calendar: "التقويم",
    inbox: "البريد",
    files: "الملفات",
    team: "الفريق",
    studio: "التخطيطات",
    library: "المكونات",
    settings: "الإعدادات",
  },
};

function Logo({ small = false }: { small?: boolean }) {
  return (
    <span className={`brand ${small ? "brand--small" : ""}`}>
      <svg viewBox="0 0 34 34" width="32" height="32" aria-hidden="true">
        <path
          d="M7 6h6l4 8 4-8h6L20 17l8 11h-7l-5-8-5 8H4l9-12z"
          fill="currentColor"
        />
      </svg>
      {!small && (
        <span>
          elyxir<span className="brand-period">.</span>
        </span>
      )}
    </span>
  );
}

class WorkspaceRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}
async function requireResponse(response: Response, fallback: string) {
  if (response.ok) return response;
  const data = await response.json().catch(() => null);
  throw new WorkspaceRequestError(
    typeof data?.error === "string" ? data.error : fallback,
    response.status,
  );
}
function readSession(value: unknown): Session {
  const candidate = value as Session | null;
  if (
    !candidate?.user ||
    !["id", "email", "name", "role"].every(
      (key) => typeof candidate.user[key as keyof Session["user"]] === "string",
    ) ||
    !["owner", "admin", "member", "viewer"].includes(candidate.user.role) ||
    !/^[0-9a-f]{64}$/.test(candidate.csrfToken)
  )
    throw new Error("The service returned an invalid session.");
  return candidate;
}

export function useWorkspace(apiUrl?: string) {
  const [state, setState] = useState<Workspace>(() => createWorkspace());
  const stateRef = useRef(state);
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const [status, setStatus] = useState<"saved" | "saving" | "error">("saved");
  const [error, setError] = useState("");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutIds = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const etag = useRef<string | null>(null);
  const saveQueue = useRef<Promise<void>>(Promise.resolve());
  const pendingSaves = useRef(0);
  const locked = useRef(false);
  const busy = useRef(false);
  const hasServerCopy = useRef(false);
  const active = useRef(true);
  const lifetime = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const base = apiUrl?.replace(/\/$/, "");
  const connectionBase = useRef(base);
  const current = (version: number) =>
    active.current && lifetime.current === version;
  const notify = useCallback(
    (message: string, kind: Toast["kind"] = "success") => {
      if (!active.current) return;
      const id = Date.now() + Math.random();
      setToasts((items) => [...items.slice(-3), { id, message, kind }]);
      const timeout = setTimeout(
        () => {
          timeoutIds.current.delete(timeout);
          if (active.current)
            setToasts((items) => items.filter((item) => item.id !== id));
        },
        kind === "error" ? 8000 : 4200,
      );
      timeoutIds.current.add(timeout);
    },
    [],
  );
  const readServerCopy = useCallback(
    async (signal?: AbortSignal) => {
      const response = await requireResponse(
        await fetch(base + "/workspace", { credentials: "include", signal }),
        "Your workspace could not be loaded.",
      );
      const workspace = validateWorkspace(await response.json());
      const revision = response.headers.get("ETag");
      if (!revision)
        throw new Error(
          "The service did not provide a workspace revision. Check that the proxy preserves ETag headers.",
        );
      return { workspace, revision };
    },
    [base],
  );
  const applyServerCopy = (copy: {
    workspace: Workspace;
    revision: string;
  }) => {
    stateRef.current = copy.workspace;
    setState(copy.workspace);
    etag.current = copy.revision;
    hasServerCopy.current = true;
    locked.current = false;
    setStatus("saved");
    setError("");
  };
  useEffect(() => {
    active.current = true;
    connectionBase.current = base;
    const version = ++lifetime.current;
    const requestController = new AbortController();
    controller.current = requestController;
    pendingSaves.current = 0;
    saveQueue.current = Promise.resolve();
    etag.current = null;
    hasServerCopy.current = false;
    sessionRef.current = null;
    setSession(null);
    busy.current = true;
    locked.current = true;
    setReady(false);
    setError("");
    setStatus("saving");
    const cleanup = () => {
      active.current = false;
      ++lifetime.current;
      requestController.abort();
      timeoutIds.current.forEach(clearTimeout);
      timeoutIds.current.clear();
    };
    if (!base) {
      try {
        const data = loadWorkspace();
        stateRef.current = data;
        setState(data);
        locked.current = false;
        setStatus("saved");
      } catch (err) {
        setStatus("error");
        setError(
          (err instanceof Error
            ? err.message
            : "Your saved workspace could not be read.") +
            " Editing is disabled to preserve the stored data. Restore or remove the elyxir.workspace.v1 browser-storage entry, then reload.",
        );
        notify(
          "Saved data could not be loaded. The sample workspace is read-only; your original stored data has been preserved.",
          "error",
        );
      }
      busy.current = false;
      setReady(true);
      return cleanup;
    }
    void (async () => {
      try {
        const response = await fetch(base + "/auth/me", {
          credentials: "include",
          signal: requestController.signal,
        });
        if (response.status === 401) {
          if (current(version)) {
            locked.current = false;
            setStatus("saved");
          }
          return;
        }
        await requireResponse(
          response,
          "The workspace service is unavailable.",
        );
        const authenticated = readSession(await response.json());
        const copy = await readServerCopy(requestController.signal);
        if (!current(version)) return;
        applyServerCopy(copy);
        sessionRef.current = authenticated;
        setSession(authenticated);
      } catch (err) {
        if (current(version) && !requestController.signal.aborted) {
          setStatus("error");
          setError(
            err instanceof Error
              ? err.message
              : "Cannot connect to the workspace service.",
          );
        }
      } finally {
        if (current(version)) {
          busy.current = false;
          setReady(true);
        }
      }
    })();
    return cleanup;
  }, [base, readServerCopy, notify]);

  const mutate = useCallback(
    (updater: (previous: Workspace) => Workspace): boolean => {
      if (!active.current || connectionBase.current !== base) return false;
      if (busy.current) {
        notify("Wait for the workspace operation to finish.", "info");
        return false;
      }
      if (locked.current) {
        notify(
          "Resolve the workspace error before making more changes.",
          "error",
        );
        return false;
      }
      if (base && (!sessionRef.current || !hasServerCopy.current)) {
        notify(
          "Sign in and load your workspace before making changes.",
          "error",
        );
        return false;
      }
      if (base && !["owner", "admin"].includes(sessionRef.current!.user.role)) {
        notify("Your account has read-only access to this workspace.", "error");
        return false;
      }
      try {
        const next = validateWorkspace(updater(stateRef.current));
        if (!base) saveWorkspace(next);
        const document = base ? JSON.stringify(next) : "";
        const version = lifetime.current;
        const authenticated = sessionRef.current;
        stateRef.current = next;
        setState(next);
        setError("");
        if (!base) {
          setStatus("saved");
          return true;
        }
        ++pendingSaves.current;
        setStatus("saving");
        saveQueue.current = saveQueue.current.then(async () => {
          try {
            if (!current(version) || locked.current) return;
            if (
              !authenticated ||
              sessionRef.current !== authenticated ||
              !etag.current
            )
              throw new Error(
                "Your session changed. Export your changes before reloading.",
              );
            const response = await fetch(base + "/workspace", {
              method: "PUT",
              credentials: "include",
              signal: controller.current?.signal,
              headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": authenticated.csrfToken,
                "If-Match": etag.current,
              },
              body: document,
            });
            if (response.status === 409)
              throw new Error(
                "The server workspace changed in another session. Export your changes, then reload the server copy.",
              );
            if (response.status === 401)
              throw new Error(
                "Your session expired. Export your changes, then reload the server copy to sign in again.",
              );
            await requireResponse(
              response,
              "Your changes could not be saved. Export a backup before reloading.",
            );
            const revision = response.headers.get("ETag");
            if (!revision)
              throw new Error(
                "The server accepted this save but returned no revision. Export your changes and reload before continuing.",
              );
            if (current(version)) etag.current = revision;
          } catch (err) {
            if (current(version)) {
              locked.current = true;
              setStatus("error");
              setError(
                err instanceof Error
                  ? err.message
                  : "Your changes could not be saved.",
              );
              notify(
                "Save failed. Your current changes remain visible; download a backup to keep them.",
                "error",
              );
            }
          } finally {
            if (current(version)) {
              pendingSaves.current = Math.max(0, pendingSaves.current - 1);
              if (!pendingSaves.current && !locked.current) setStatus("saved");
            }
          }
        });
        return true;
      } catch (err) {
        notify(
          err instanceof Error
            ? err.message
            : "This change could not be saved.",
          "error",
        );
        return false;
      }
    },
    [base, notify],
  );

  const login = async (email: string, password: string) => {
    if (!base || busy.current)
      throw new Error("Wait for the workspace connection to finish.");
    const version = lifetime.current;
    busy.current = true;
    locked.current = true;
    try {
      const response = await requireResponse(
        await fetch(base + "/auth/login", {
          method: "POST",
          credentials: "include",
          signal: controller.current?.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        }),
        "Check your email and password.",
      );
      const authenticated = readSession(await response.json());
      const copy = await readServerCopy(controller.current?.signal);
      if (!current(version)) return;
      applyServerCopy(copy);
      sessionRef.current = authenticated;
      setSession(authenticated);
    } catch (err) {
      if (current(version)) {
        setStatus("error");
        setError(err instanceof Error ? err.message : "Unable to sign in.");
      }
      throw err;
    } finally {
      if (current(version)) busy.current = false;
    }
  };
  const reload = async () => {
    if (!base || !sessionRef.current)
      throw new Error("Sign in to load the server workspace.");
    if (busy.current || pendingSaves.current)
      throw new Error(
        "Wait for pending saves to finish before reloading the server copy.",
      );
    const version = lifetime.current;
    busy.current = true;
    locked.current = true;
    setStatus("saving");
    try {
      const copy = await readServerCopy(controller.current?.signal);
      if (current(version)) applyServerCopy(copy);
    } catch (err) {
      if (current(version)) {
        setStatus("error");
        setError(
          err instanceof Error
            ? err.message
            : "The server copy could not be loaded.",
        );
        if (err instanceof WorkspaceRequestError && err.status === 401) {
          sessionRef.current = null;
          setSession(null);
          hasServerCopy.current = false;
        }
      }
      throw err;
    } finally {
      if (current(version)) busy.current = false;
    }
  };
  const logout = async () => {
    if (!base || !sessionRef.current || busy.current) return;
    const version = lifetime.current;
    busy.current = true;
    try {
      await saveQueue.current;
      if (!current(version)) return;
      if (locked.current) {
        notify(
          "Export your changes and resolve the save error before signing out.",
          "error",
        );
        return;
      }
      const authenticated = sessionRef.current;
      if (!authenticated) return;
      const response = await fetch(base + "/auth/logout", {
        method: "POST",
        credentials: "include",
        signal: controller.current?.signal,
        headers: { "X-CSRF-Token": authenticated.csrfToken },
      });
      if (response.status !== 401)
        await requireResponse(
          response,
          "Could not sign out. Please try again.",
        );
      if (!current(version)) return;
      sessionRef.current = null;
      setSession(null);
      const sample = createWorkspace();
      stateRef.current = sample;
      setState(sample);
      etag.current = null;
      hasServerCopy.current = false;
      setError("");
      setStatus("saved");
      locked.current = false;
    } catch (err) {
      if (current(version))
        notify(
          err instanceof Error
            ? err.message
            : "Could not sign out. Please try again.",
          "error",
        );
    } finally {
      if (current(version)) busy.current = false;
    }
  };
  return {
    state,
    mutate,
    notify,
    toasts,
    setToasts,
    ready,
    session,
    status,
    error,
    login,
    logout,
    reload,
  };
}

function Login({
  onLogin,
  error: serviceError,
}: {
  onLogin: (email: string, password: string) => Promise<void>;
  error: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const data = new FormData(e.currentTarget);
    try {
      await onLogin(String(data.get("email")), String(data.get("password")));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to sign in.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="login-page">
      <section className="login-story">
        <Logo />
        <div>
          <span className="eyebrow">
            A LITTLE CLARITY. A LOT OF POSSIBILITY.
          </span>
          <h1>
            Good work starts
            <br />
            with a little space.
          </h1>
          <p>
            Your people, projects and possibilities.
            <br />
            Thoughtfully brought together.
          </p>
          <div className="login-art" aria-hidden="true">
            <div />
            <div />
            <div />
            <Sparkles size={36} />
          </div>
        </div>
        <span>Made for the way you work.</span>
      </section>
      <section className="login-form-wrap">
        <form onSubmit={submit}>
          <div className="login-lock">
            <ShieldCheck size={24} />
          </div>
          <h2>Welcome back.</h2>
          <p>A little closer to your next big thing.</p>
          {(error || serviceError) && (
            <p role="alert" className="form-error">
              {error || serviceError}
            </p>
          )}
          <Field label="Email address">
            <input
              type="email"
              name="email"
              className="input"
              autoComplete="username"
              placeholder="you@company.com"
              required
            />
          </Field>
          <Field label="Password">
            <input
              type="password"
              name="password"
              className="input"
              autoComplete="current-password"
              required
              minLength={12}
            />
          </Field>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Signing in…" : "Step inside"}
            <ArrowRight size={16} />
          </Button>
          <p className="login-note">
            Use the administrator credentials configured for this workspace.
          </p>
        </form>
        <footer>
          Elyxir Workspace <span>Secure session authentication</span>
        </footer>
      </section>
    </main>
  );
}

class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: boolean }
> {
  state = { error: false };
  static getDerivedStateFromError() {
    return { error: true };
  }
  render() {
    return this.state.error ? (
      <main className="fatal-error">
        <Logo />
        <h1>A little pause.</h1>
        <p>
          This screen could not load. Your saved workspace is still in your
          browser or connected server.
        </p>
        <Button onClick={() => window.location.reload()}>
          Reload workspace
        </Button>
      </main>
    ) : (
      this.props.children
    );
  }
}

export default function App({ apiUrl }: { apiUrl?: string } = {}) {
  return (
    <ErrorBoundary>
      <WorkspaceApp apiUrl={apiUrl} />
    </ErrorBoundary>
  );
}

function WorkspaceApp({ apiUrl }: { apiUrl?: string }) {
  const {
    state,
    mutate,
    notify,
    toasts,
    setToasts,
    ready,
    session,
    status,
    error,
    login,
    logout,
    reload,
  } = useWorkspace(apiUrl);
  const [route, setRoute] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [appearance, setAppearance] = useState(false);
  const [notifications, setNotifications] = useState(false);
  const [quickCreate, setQuickCreate] = useState(false);
  const [help, setHelp] = useState(false);
  const [systemDark, setSystemDark] = useState(false);
  const [activeSearch, setActiveSearch] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const sync = () => {
      setIsMobile(media.matches);
      if (!media.matches) setMobileOpen(false);
    };
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);
  const routeId = route.split("?")[0];
  const currentNav = allNav.find((n) => n.id === routeId);
  const dark =
    state.settings.theme === "dark" ||
    (state.settings.theme === "system" && systemDark);
  const navigate = useCallback((path: string) => {
    window.location.hash = path;
    setRoute(path);
    setMobileOpen(false);
    setSearchOpen(false);
    setQuickCreate(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);
  useEffect(() => {
    const sync = () =>
      setRoute(window.location.hash.replace(/^#\/?/, "") || "overview");
    sync();
    window.addEventListener("hashchange", sync);
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    setSystemDark(media.matches);
    const onMedia = () => setSystemDark(media.matches);
    media.addEventListener("change", onMedia);
    return () => {
      window.removeEventListener("hashchange", sync);
      media.removeEventListener("change", onMedia);
    };
  }, []);
  useEffect(() => {
    document.documentElement.dataset.elyxirTheme = dark ? "dark" : "light";
    document.documentElement.dataset.elyxirColorway = colorwayKey(
      state.settings.colorway,
    );
    const selectedAccent = accentValue(state.settings);
    document.documentElement.style.setProperty("--accent", selectedAccent);
    document.documentElement.style.setProperty(
      "--accent-ink",
      accentInk(selectedAccent),
    );
    document.documentElement.dir = state.settings.direction;
    document.documentElement.lang = state.settings.locale;
    document.title = `${currentNav?.name || "Page not found"} · Elyxir`;
  }, [
    dark,
    state.settings.accent,
    state.settings.customAccent,
    state.settings.colorway,
    state.settings.direction,
    state.settings.locale,
    currentNav?.name,
  ]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((open) => !open);
        setSearch("");
      }
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    document.body.style.overflow = "hidden";
    const focusable = () =>
      Array.from(
        sidebarRef.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], [tabindex="0"]',
        ) ?? [],
      ).filter((element) => element.getClientRects().length > 0);
    focusable()[0]?.focus();
    const trap = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const items = focusable();
      const first = items[0],
        last = items.at(-1);
      if (!first) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", trap);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", trap);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [mobileOpen]);
  const unread = state.messages.filter(
    (m) => m.folder === "inbox" && !m.read,
  ).length;
  const searchResults = [
    ...allNav.map((n) => ({
      label: n.name,
      description: "Go to page",
      path: n.id,
      icon: n.icon,
    })),
    ...state.contacts.map((c) => ({
      label: c.name,
      description: c.company,
      path: "contacts",
      icon: Users,
    })),
    ...state.projects.map((p) => ({
      label: p.name,
      description: "Project",
      path: `tasks?project=${p.id}`,
      icon: FolderKanban,
    })),
    ...state.tasks.map((t) => ({
      label: t.title,
      description: "Task",
      path: `tasks?project=${t.projectId}`,
      icon: Check,
    })),
  ]
    .filter((item) =>
      `${item.label} ${item.description}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    )
    .slice(0, 9);
  useEffect(() => setActiveSearch(0), [search]);
  const props: PageProps = { state, mutate, notify, navigate };
  const pages: Record<string, ReactNode> = {
    overview: <Dashboard {...props} />,
    analytics: <AnalyticsPage {...props} />,
    contacts: <ContactsPage {...props} />,
    pipeline: <PipelinePage {...props} />,
    projects: <ProjectsPage {...props} />,
    tasks: <TasksPage key={route} {...props} />,
    products: <ProductsPage {...props} />,
    orders: <OrdersPage {...props} />,
    invoices: <InvoicesPage {...props} />,
    calendar: <CalendarPage {...props} />,
    inbox: <InboxPage {...props} />,
    files: <FilesPage {...props} />,
    team: <TeamPage {...props} />,
    settings: <SettingsPage {...props} />,
    studio: <StudioPage {...props} />,
    library: <LibraryPage {...props} />,
  };
  if (!ready)
    return (
      <main className="loading-workspace">
        <Logo />
        <span className="loading-line" />
        <p>Making a little room for your work…</p>
      </main>
    );
  if (apiUrl && !session) return <Login onLogin={login} error={error} />;
  const renderNav = () =>
    navGroups.map((group) => (
      <div className="nav-group" key={group.label}>
        <p>{group.label}</p>
        {group.items.map((item) => (
          <button
            key={item.id}
            className={`nav-item ${routeId === item.id ? "active" : ""}`}
            aria-current={routeId === item.id ? "page" : undefined}
            title={item.name}
            onClick={() => navigate(item.id)}
          >
            <item.icon size={18} strokeWidth={1.7} />
            <span>
              {translations[state.settings.locale]?.[item.id] || item.name}
            </span>
            {item.id === "inbox" && unread > 0 && <b>{unread}</b>}
            {item.id === "studio" && <span className="nav-new">NEW</span>}
          </button>
        ))}
      </div>
    ));
  return (
    <div
      className={`elyxir-app ${dark ? "theme-dark" : "theme-light"} layout-${state.settings.layout} density-${state.settings.density}`}
      dir={state.settings.direction}
    >
      <a
        className="skip-link"
        href="#main-content"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById("main-content")?.focus();
        }}
      >
        Skip to content
      </a>
      {mobileOpen && (
        <button
          className="mobile-backdrop"
          tabIndex={-1}
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}
      {state.settings.layout === "dual" && (
        <aside className="dual-rail" aria-label="Quick navigation">
          <Logo small />
          <button
            title="Overview"
            aria-label="Overview"
            onClick={() => navigate("overview")}
          >
            <LayoutDashboard size={20} />
          </button>
          <button
            title="Projects"
            aria-label="Projects"
            onClick={() => navigate("projects")}
          >
            <FolderKanban size={20} />
          </button>
          <button
            title="Contacts"
            aria-label="Contacts"
            onClick={() => navigate("contacts")}
          >
            <Users size={20} />
          </button>
          <button
            title="Settings"
            aria-label="Settings"
            onClick={() => navigate("settings")}
          >
            <Settings2 size={20} />
          </button>
        </aside>
      )}
      <aside
        ref={sidebarRef}
        className={`sidebar ${mobileOpen ? "sidebar--open" : ""}`}
        inert={isMobile && !mobileOpen}
        role={mobileOpen ? "dialog" : undefined}
        aria-modal={mobileOpen || undefined}
        aria-label="Main navigation"
      >
        <div className="sidebar-brand">
          <button onClick={() => navigate("overview")} aria-label="Elyxir home">
            <Logo />
          </button>
          <IconButton
            label="Close navigation"
            className="sidebar-close"
            onClick={() => setMobileOpen(false)}
          >
            <X size={18} />
          </IconButton>
          <span className="brand-version">WORKSPACE</span>
        </div>
        <button
          className="workspace-switch"
          onClick={() => navigate("settings")}
        >
          <span className="workspace-monogram">
            {state.settings.workspace[0]}
          </span>
          <span>
            <strong>{state.settings.workspace}</strong>
            <small>Your workspace</small>
          </span>
          <ChevronDown size={15} />
        </button>
        <nav className="main-nav">{renderNav()}</nav>
        <div className="sidebar-bottom">
          <div className="sidebar-note">
            <span className="sidebar-note-icon">
              <Sparkles size={18} />
            </span>
            <strong>Make it feel like you.</strong>
            <p>
              A thoughtful layout for
              <br />
              every kind of work.
            </p>
            <button onClick={() => navigate("studio")}>
              Explore layouts <ArrowUpRight size={14} />
            </button>
          </div>
          <button className="sidebar-help" onClick={() => setHelp(true)}>
            <CircleHelp size={17} />
            <span>A little help</span>
            <ArrowUpRight size={14} />
          </button>
          <button
            className="sidebar-profile"
            onClick={() => navigate("settings")}
          >
            <Avatar name={state.settings.name} color="#DEE5F4" />
            <span>
              <strong>{state.settings.name}</strong>
              <small>{state.settings.role}</small>
            </span>
            <ChevronDown size={15} />
          </button>
        </div>
      </aside>
      <div className="main-shell" inert={isMobile && mobileOpen}>
        <header className="topbar">
          <div className="topbar-left">
            <IconButton
              label="Open navigation"
              className="mobile-menu-button"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={21} />
            </IconButton>
            <span className="topbar-crumb">
              Workspace <ChevronRight size={13} />
              <strong>{currentNav?.name || "Not found"}</strong>
            </span>
            <span className="topbar-logo">
              <Logo />
            </span>
          </div>
          <div className="topbar-actions">
            <button
              className="global-search"
              aria-label="Search workspace"
              onClick={() => {
                setSearch("");
                setSearchOpen(true);
              }}
            >
              <Search size={17} />
              <span>Search anything…</span>
              <kbd>⌘ K</kbd>
            </button>
            <span
              className={`save-state save-state--${status}`}
              title={
                apiUrl
                  ? "Connected workspace API"
                  : "Saved locally in this browser"
              }
            >
              <span />
              {status === "saving"
                ? "Saving…"
                : status === "error"
                  ? "Save failed"
                  : apiUrl
                    ? "Connected"
                    : "Local demo"}
            </span>
            <span className="topbar-divider" />
            <IconButton
              label="Customize appearance"
              onClick={() => setAppearance(true)}
            >
              <SlidersHorizontal size={18} />
            </IconButton>
            <IconButton
              label={`Notifications${state.settings.notifications ? ", " + state.activity.length + " activities" : ""}`}
              className="notification-trigger"
              onClick={() => setNotifications(true)}
            >
              <Bell size={18} />
              {state.settings.notifications && <i />}
            </IconButton>
            <button
              className="topbar-avatar"
              aria-label="My profile"
              onClick={() => navigate("settings")}
            >
              <Avatar name={state.settings.name} color="#E5E9F5" size="sm" />
            </button>
            {apiUrl && (
              <IconButton label="Sign out" onClick={() => void logout()}>
                <LogOut size={18} />
              </IconButton>
            )}
          </div>
        </header>
        {(state.settings.layout === "horizontal" ||
          state.settings.layout === "minimal") && (
          <nav className="horizontal-nav" aria-label="Page navigation">
            {allNav.map((n) => (
              <button
                key={n.id}
                className={routeId === n.id ? "active" : ""}
                onClick={() => navigate(n.id)}
              >
                <n.icon size={16} />
                {translations[state.settings.locale]?.[n.id] || n.name}
              </button>
            ))}
          </nav>
        )}
        {error && (
          <div className="workspace-error" role="alert">
            <span>{error}</span>
            <Button
              onClick={() =>
                downloadFile(
                  "elyxir-unsaved-backup.json",
                  JSON.stringify(state, null, 2),
                  "application/json",
                )
              }
            >
              <Download size={14} />
              Backup
            </Button>
            {apiUrl && (
              <Button
                onClick={() =>
                  void reload().catch((err) => notify(err.message, "error"))
                }
              >
                Reload server copy
              </Button>
            )}
          </div>
        )}
        <main id="main-content" tabIndex={-1} className="page-content">
          <Suspense fallback={<p>Loading your workspace…</p>}>
            {pages[routeId] || (
              <EmptyState
                icon={<Search size={30} />}
                title="This space hasn’t been built yet."
                description="Let’s take you back to somewhere familiar."
                action={
                  <Button
                    variant="primary"
                    onClick={() => navigate("overview")}
                  >
                    Back to overview
                  </Button>
                }
              />
            )}
          </Suspense>
        </main>
        <footer className="workspace-footer">
          <span>
            elyxir<span> · </span>Made for the way you work.
          </span>
          <button onClick={() => navigate("library")}>
            Documentation <ArrowUpRight size={13} />
          </button>
        </footer>
      </div>
      <button
        inert={isMobile && mobileOpen}
        className="floating-customizer"
        aria-label="Open quick actions"
        onClick={() => setQuickCreate(true)}
      >
        <Plus size={23} />
      </button>
      <div className="toast-region" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast toast--${toast.kind}`}
            role={toast.kind === "error" ? "alert" : "status"}
          >
            <span>
              {toast.kind === "success" ? (
                <Check size={17} />
              ) : toast.kind === "error" ? (
                <X size={17} />
              ) : (
                <Sparkles size={17} />
              )}
            </span>
            <p>{toast.message}</p>
            <IconButton
              label="Dismiss notification"
              onClick={() =>
                setToasts((items) => items.filter((t) => t.id !== toast.id))
              }
            >
              <X size={14} />
            </IconButton>
          </div>
        ))}
      </div>
      <Modal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Find your next step."
        description="Search pages, people, projects and tasks."
        wide
      >
        <div className="command-search">
          <Search size={21} />
          <input
            ref={searchRef}
            autoFocus
            aria-label="Search workspace"
            placeholder="What are you looking for?"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveSearch((i) =>
                  Math.min(i + 1, searchResults.length - 1),
                );
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveSearch((i) => Math.max(i - 1, 0));
              }
              if (e.key === "Enter" && searchResults[activeSearch])
                navigate(searchResults[activeSearch].path);
            }}
          />
        </div>
        <div className="command-results">
          {searchResults.map((result, i) => (
            <button
              key={`${result.path}-${result.label}`}
              className={activeSearch === i ? "active" : ""}
              onMouseEnter={() => setActiveSearch(i)}
              onClick={() => navigate(result.path)}
            >
              <result.icon size={18} />
              <span>
                <strong>{result.label}</strong>
                <small>{result.description}</small>
              </span>
              <ArrowUpRight size={15} />
            </button>
          ))}
          {!searchResults.length && (
            <EmptyState
              title="Nothing here just yet"
              description="Try a different name or page title."
            />
          )}
        </div>
        <div className="command-hint">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> to explore
          </span>
          <span>
            <kbd>↵</kbd> to open
          </span>
          <span>
            <kbd>esc</kbd> to close
          </span>
        </div>
      </Modal>
      <Modal
        open={appearance}
        onClose={() => setAppearance(false)}
        title="A little more you."
        description="Make your workspace feel like home."
      >
        <div className="customizer-section">
          <h3>Your workspace atmosphere</h3>
          <div className="colorway-choices">
            {colorways.map((colorway) => (
              <button
                key={colorway.id}
                className={
                  colorwayKey(state.settings.colorway) === colorway.id
                    ? "selected"
                    : ""
                }
                aria-pressed={
                  colorwayKey(state.settings.colorway) === colorway.id
                }
                onClick={() =>
                  mutate((s) => ({
                    ...s,
                    settings: { ...s.settings, colorway: colorway.id },
                  }))
                }
              >
                <span
                  className={`colorway-swatch colorway-swatch--${colorway.id}`}
                >
                  <i />
                  <b />
                  <em />
                </span>
                <strong>
                  {colorway.name}
                  {colorwayKey(state.settings.colorway) === colorway.id && (
                    <Check size={13} />
                  )}
                </strong>
              </button>
            ))}
          </div>
        </div>
        <div className="customizer-section">
          <h3>Your preferred light</h3>
          <div className="segmented">
            {(
              [
                { id: "light", name: "Light", icon: Sun },
                { id: "dark", name: "Dark", icon: Moon },
                { id: "system", name: "System", icon: Globe2 },
              ] as const
            ).map((theme) => (
              <button
                key={theme.id}
                className={state.settings.theme === theme.id ? "active" : ""}
                onClick={() =>
                  mutate((s) => ({
                    ...s,
                    settings: { ...s.settings, theme: theme.id },
                  }))
                }
              >
                <theme.icon size={16} />
                {theme.name}
              </button>
            ))}
          </div>
        </div>
        <div className="customizer-section">
          <h3>A signature accent</h3>
          <AccentPicker
            settings={state.settings}
            onChange={(partial) =>
              mutate((s) => ({
                ...s,
                settings: { ...s.settings, ...partial },
              }))
            }
          />
        </div>
        <div className="customizer-section">
          <Field label="Your layout">
            <select
              className="select"
              value={state.settings.layout}
              onChange={(e) =>
                mutate((s) => ({
                  ...s,
                  settings: {
                    ...s.settings,
                    layout: e.target.value as Workspace["settings"]["layout"],
                  },
                }))
              }
            >
              {layouts.map((layout) => (
                <option key={layout.id} value={layout.id}>
                  {layout.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="form-actions">
          <Button
            variant="ghost"
            onClick={() => {
              setAppearance(false);
              navigate("studio");
            }}
          >
            Explore all layouts <ArrowRight size={15} />
          </Button>
          <Button variant="primary" onClick={() => setAppearance(false)}>
            Looks good <Check size={15} />
          </Button>
        </div>
      </Modal>
      <Modal
        open={notifications}
        onClose={() => setNotifications(false)}
        title="A little pulse on your workspace."
        description={
          state.settings.notifications
            ? "Recent activity from the fictional sample workspace."
            : "Activity notifications are turned off."
        }
      >
        {state.settings.notifications ? (
          <div className="activity-feed">
            {state.activity.slice(0, 8).map((activity) => (
              <article key={activity.id}>
                <span className={`activity-dot activity-${activity.kind}`}>
                  <Activity size={16} />
                </span>
                <div>
                  <strong>{activity.title}</strong>
                  <p>{activity.description}</p>
                  <time>
                    {new Date(activity.time).toLocaleString(
                      state.settings.locale,
                      {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </time>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Bell />}
            title="A little peace and quiet"
            description="You can enable activity notifications in preferences."
            action={
              <Button
                onClick={() => {
                  setNotifications(false);
                  navigate("settings");
                }}
              >
                Open settings
              </Button>
            }
          />
        )}
      </Modal>
      <Modal
        open={quickCreate}
        onClose={() => setQuickCreate(false)}
        title="What’s your next move?"
        description="Good things start with a small step."
      >
        <div className="quick-actions">
          {[
            {
              title: "A new project",
              text: "Give an idea room to grow.",
              icon: FolderKanban,
              path: "projects",
            },
            {
              title: "A new connection",
              text: "Bring someone into your circle.",
              icon: Users,
              path: "contacts",
            },
            {
              title: "A little focus",
              text: "Find your next task.",
              icon: LayoutGrid,
              path: "tasks",
            },
            {
              title: "A fresh invoice",
              text: "Keep your business moving.",
              icon: FileText,
              path: "invoices",
            },
            {
              title: "A moment together",
              text: "Make space on the calendar.",
              icon: CalendarDays,
              path: "calendar",
            },
          ].map((action) => (
            <button key={action.path} onClick={() => navigate(action.path)}>
              <action.icon size={21} />
              <span>
                <strong>{action.title}</strong>
                <small>{action.text}</small>
              </span>
              <ArrowUpRight size={18} />
            </button>
          ))}
        </div>
      </Modal>
      <Modal
        open={help}
        onClose={() => setHelp(false)}
        title="A little help, when you need it."
        description="Find your way around Elyxir."
      >
        <div className="help-content">
          <div>
            <Command size={20} />
            <span>
              <strong>Find anything, quickly.</strong>
              <p>Press ⌘K or Ctrl+K to search your workspace.</p>
            </span>
          </div>
          <div>
            <Palette size={20} />
            <span>
              <strong>Make yourself at home.</strong>
              <p>
                Use the appearance button to switch themes, accents and layouts.
              </p>
            </span>
          </div>
          <div>
            <ShieldCheck size={20} />
            <span>
              <strong>Your work stays with you.</strong>
              <p>
                Export a backup from Settings → Workspace data. The default demo
                stores data in this browser.
              </p>
            </span>
          </div>
        </div>
        <div className="form-actions">
          <Button
            variant="primary"
            onClick={() => {
              setHelp(false);
              navigate("library");
            }}
          >
            Explore the documentation <ArrowRight size={16} />
          </Button>
        </div>
      </Modal>
    </div>
  );
}
